import React from "react";
import { Tag, message } from "antd";
import Service from "../../service";
import { getRoles } from "../../util/getRoles";

// ── Role helpers ────────────────────────────────────────────────────────────
// Mirrors the backend role gating (section 8 of the plan):
//   User            → own advances/expenses only
//   Reporting Manager → team scope
//   Account Team    → org-wide (finance + settlement)
//   Admin / HR      → config + org-wide
export const isAccountTeam = () => getRoles(["Account Team"]);
export const isReportingManager = () =>
  getRoles(["Reporting Manager", "Assistance Reporting Manager"]);
export const isAdminOrHR = () => getRoles(["Admin", "HR"]);
// Sub-Admin reads expenses org-wide like Admin (finance dashboard and reports).
export const isSubAdmin = () => getRoles(["Sub-Admin"]);

// Which dashboard the current user should see.
export const resolveDashboardScope = () => {
  if (isAccountTeam() || isAdminOrHR() || isSubAdmin()) return "finance";
  if (isReportingManager()) return "manager";
  return "employee";
};

// ── Currency (dynamic - configured per company in Expense Config) ────────────
const CURRENCY_STORAGE_KEY = "expense_currency_symbol";
const DEFAULT_CURRENCY_SYMBOL = "₹";

// Read the company's configured currency symbol (cached from the last config
// fetch); falls back to ₹. Safe against unavailable localStorage.
export const getExpenseCurrencySymbol = () => {
  try {
    return localStorage.getItem(CURRENCY_STORAGE_KEY) || DEFAULT_CURRENCY_SYMBOL;
  } catch {
    return DEFAULT_CURRENCY_SYMBOL;
  }
};

// Cache the configured symbol so formatMoney / input prefixes pick it up.
export const setExpenseCurrencySymbol = (symbol) => {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, symbol || DEFAULT_CURRENCY_SYMBOL);
  } catch {
    /* ignore */
  }
};

// ── Formatting ──────────────────────────────────────────────────────────────
// "<symbol> 1,20,000.00" - dynamic symbol, with a space between sign and digits.
export const formatMoney = (amount) =>
  `${getExpenseCurrencySymbol()} ${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_COLORS = {
  issued: "blue",
  pending: "orange",
  hold: "gold",
  requestchanges: "purple",
  partiallysettled: "geekblue",
  approved: "green",
  rejected: "red",
  settled: "cyan",
  closed: "default",
};

export const statusTag = (status) => {
  const raw = String(status || "");
  const key = raw.toLowerCase().replace(/\s+/g, "");
  // Humanize camelCase statuses for display: "PartiallySettled" → "Partially
  // Settled", "RequestChanges" → "Request Changes".
  const label = raw ? raw.replace(/([a-z])([A-Z])/g, "$1 $2") : "-";
  return (
    <Tag
      color={STATUS_COLORS[key] || "default"}
      style={{ margin: 0, verticalAlign: "middle" }}
    >
      {label}
    </Tag>
  );
};

// ── File validation (JPG / PNG / PDF, max 5) ────────────────────────────────
export const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";
export const MAX_ATTACHMENTS = 5;

const ALLOWED_MIME = ["image/jpeg", "image/png", "application/pdf"];

// beforeUpload handler for an Ant Upload - validates type & count, keeps files
// client-side (returns false so Ant does not auto-upload). Returns Upload.LIST_IGNORE
// for invalid files so they never enter the list.
export const validateAttachment = (file, currentList) => {
  const okType =
    ALLOWED_MIME.includes(file.type) ||
    /\.(jpe?g|png|pdf)$/i.test(file.name || "");
  if (!okType) {
    message.error(`${file.name}: only JPG, PNG and PDF files are allowed.`);
    return false;
  }
  if ((currentList?.length || 0) >= MAX_ATTACHMENTS) {
    message.error(`You can attach at most ${MAX_ATTACHMENTS} files.`);
    return false;
  }
  return true;
};

// ── Thin API wrappers around the shared Service.makeAPICall ─────────────────
// Every call reuses Service.makeAPICall so the auth token + interceptors +
// base URL are handled centrally (no hardcoded URLs/tokens).
const unwrap = (response, fallback = "Request failed") => {
  const payload = response?.data;
  // Backend success is configs.success_status (200); some legacy endpoints
  // use 1. Treat both as success.
  if (payload && (payload.status === 200 || payload.status === 1)) {
    return payload.data;
  }
  throw new Error(payload?.message || fallback);
};

export const expenseApi = {
  get: async (api_url, params) => {
    const response = await Service.makeAPICall({
      methodName: Service.getMethod,
      api_url,
      params,
    });
    return unwrap(response);
  },
  post: async (api_url, body, options) => {
    const response = await Service.makeAPICall({
      methodName: Service.postMethod,
      api_url,
      body,
      options,
    });
    return unwrap(response);
  },
  put: async (api_url, body) => {
    const response = await Service.makeAPICall({
      methodName: Service.putMethod,
      api_url,
      body,
    });
    return unwrap(response);
  },
  del: async (api_url, body) => {
    const response = await Service.makeAPICall({
      methodName: Service.deleteMethod,
      api_url,
      body,
    });
    return unwrap(response);
  },
};

// Fetch the employee dropdown used by advance/report filters.
export const fetchEmployeeOptions = async () => {
  const response = await Service.makeAPICall({
    methodName: Service.postMethod,
    api_url: Service.empDropdownList,
  });
  return response?.data?.data || [];
};

// Trigger a file download for a report export (?format=excel|pdf).
//
// Excel is routed through the shared Service.makeAPICall so the auth token +
// base URL flow through the central interceptor (no hardcoded URLs/tokens). The
// backend returns a base64 payload which we turn into a data-URI anchor download,
// mirroring AdminBillReimbursement.handleExportCSV.
//
// PDF is fetched directly (bypassing makeAPICall) since the backend now streams
// it as a raw binary response, the same way EmployeeLeftReport's exportPdf does
// - a plain fetch + response.blob() + object URL download, with the auth token
// attached by hand since the shared interceptor isn't in play here.
export const downloadReportExport = async (reportKey, format, params = {}) => {
  const query = new URLSearchParams({ ...params, format }).toString();
  const api_url = `${Service.expenseReport}/${reportKey}?${query}`;

  if (format === "pdf") {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${Service.API_URL}${api_url}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        message.error("Export failed");
        return;
      }
      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = fileURL;
      downloadLink.download = `${reportKey}.pdf`;
      downloadLink.style.display = "none";
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(fileURL);
      message.success("Export successful");
    } catch (err) {
      message.error(err.message || "Export failed");
    }
    return;
  }

  const mimeMap = {
    excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const extMap = { excel: "xlsx" };
  const mime = mimeMap[format] || "application/octet-stream";
  const ext = extMap[format] || format;
  try {
    const response = await Service.makeAPICall({
      methodName: Service.getMethod,
      api_url,
    });
    const base64 = response?.data?.data;
    if ((response?.data?.status === 200 || response?.data?.status === 1) && base64) {
      const linkSource = `data:${mime};base64,${base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = `${reportKey}.${ext}`;
      downloadLink.style.display = "none";
      downloadLink.click();
      downloadLink.remove();
      message.success(response?.data?.message || "Export successful");
    } else {
      message.error(response?.data?.message || "Export failed");
    }
  } catch (err) {
    message.error(err.message || "Export failed");
  }
};

export const APPROVAL_ACTIONS = [
  "Approve",
  "Reject",
  "RequestChanges",
  "Hold",
  "Comment",
];

export const SALARY_GRADES = ["A", "B", "C", "D", "E", "F"];
